import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { Book, classNameMap, ErnotsAbstractModel, ModelClass } from './models'
import { RequestStatus } from '../api_request/api_request';
import { useAppSelector } from '../store/hooks';

/**
 * A dictionary of all the objects of a particular type that have been created or downloaded.
 * Note that they must be plain objects (such as {}), not class instances, because React's state doesn't like that.
 */
export type ModelMap<T extends ErnotsAbstractModel = ErnotsAbstractModel> = {[key: string]: T}

/**
 * Maps class names to ModelMap instances.
 */
export type ModelState =
{
    [key: string]:
    {
        models: ModelMap;
        request: RequestStatus;
    }
};

export function getModelMap<T extends ErnotsAbstractModel>(appSelector: typeof useAppSelector, modelClass: ModelClass<T>)
{
    const mcn = classNameMap[modelClass.name];
    return appSelector((state) => state.rest[mcn]?.models as ModelMap<T> | undefined);
}

export function getModelArr<T extends ErnotsAbstractModel>(appSelector: typeof useAppSelector, modelClass: ModelClass<T>)
{
    const map = getModelMap(appSelector, modelClass);
    return map ? Object.values(map) : undefined;
}

/** Used to replace an entire ModelMap in the state. */
type ModelMapReplacement<T extends ErnotsAbstractModel> =
{
    modelClassName: string;
    models: ModelMap<T>;
}

type RequestUpdate =
{
    modelClassName: string;
    request: RequestStatus;
}

type BookmarkUpdate =
{
    bookId: string;
    sentenceId: number;
}

//Arguments to these functions (the T object) must be plain objects, not instances.
const restSlice = createSlice(
{
    name: 'rest_models',
    initialState: {} as ModelState,
    reducers:
    {
        /**
         * Add a single REST model to the dictionary corresponding to its modelClassName.
         * If model with this key exists, it is replaced.
         */
        setModel: function(state: ModelState, action: PayloadAction<ErnotsAbstractModel>)
        {
            const model = action.payload;
            const entry = state[model.modelClassName] = state[model.modelClassName] ?? {};
            const map = entry.models = entry.models ?? {};

            map[model.key] = model;
            
        },

        /**
         * Remove a model, using its modelClassName and key to find it.
         */
        unsetModel: function(state: ModelState, action: PayloadAction<ErnotsAbstractModel>)
        {
            const model = action.payload;
            delete state[model.modelClassName]?.models?.[model.key];
        },

        /**
         * Replace an entire dictionary of models for a particular model class.
         * For example, if the program refreshes the list of the user's books.
         */
        setModels: function<T extends ErnotsAbstractModel>(state: ModelState, action: PayloadAction<ModelMapReplacement<T>>)
        {
            const model = action.payload;
            const entry = state[model.modelClassName] = state[model.modelClassName] ?? {};
            entry.models = action.payload.models;
        },

        /**
         * Replace an entire dictionary of models for a particular model class.
         * For example, if the program refreshes the list of the user's books.
         */
        setRequest: function(state: ModelState, action: PayloadAction<RequestUpdate>)
        {
            const model = action.payload;
            const entry = state[model.modelClassName] = state[model.modelClassName] ?? {};
            entry.request = action.payload.request;
        },

        setBookmark: function(state: ModelState, action: PayloadAction<BookmarkUpdate>)
        {
            const book = state[Book.name]?.models?.[action.payload.bookId] as Book;
            if (book)
            {
                book.bookmark = action.payload.sentenceId;
            }
        },
    },
});

export const restActions = restSlice.actions;
export const restReducer = restSlice.reducer;